import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export type PlaceSourceKey = "osm" | "datatourisme";
export type PlacePoolStatus = "unmatched" | "linked" | "dismissed";

export interface SourcePlaceData {
    source: PlaceSourceKey;
    sourceId: string;
    name?: string;
    street?: string;
    postcode?: string;
    city?: string;
    lat?: number;
    lon?: number;
    phone?: string;
    website?: string;
    email?: string;
    category?: string;
    cuisine?: string[];
    openingHoursRaw?: string;
    socials?: Record<string, string>;
    updatedAt?: string;
}

export interface PooledPlace {
    _id: string;
    source: PlaceSourceKey;
    sourceId: string;
    name?: string;
    postcode?: string;
    city?: string;
    data: SourcePlaceData;
    status: PlacePoolStatus;
    active: boolean;
    firstSeen: string;
    lastSeen: string;
}

export interface PlaceImportStats {
    records: number;
    matched: number;
    written: number;
    unchanged: number;
    high: number;
    unverified: number;
    approved: number;
    skipped: number;
    pooled: number;
    eventsCreated: number;
}

export interface PlaceImportFile {
    name: string;
    source?: PlaceSourceKey;
    /** matching = linking places to establishments · resolving = finding their events · writing = writing / approving */
    status: "queued" | "matching" | "resolving" | "writing" | "done" | "error";
    total?: number;
    done: number;
    stats: PlaceImportStats;
    meta?: { generatedAt?: string; count?: number; complete?: boolean; notes?: string[] };
    error?: string;
    startedAt?: string;
    finishedAt?: string;
}

export interface PlaceImportState {
    importId: string;
    running: boolean;
    startedAt: string;
    finishedAt?: string;
    files: PlaceImportFile[];
}

export interface PlaceSourcesStatus {
    import: PlaceImportState | null;
    sources: Record<
        PlaceSourceKey,
        {
            last: { at: string; file: string; generatedAt?: string; complete?: boolean; notes?: string[]; stats: PlaceImportStats } | null;
            pool: { unmatched: number; dismissed: number; linked: number; inactive: number };
        }
    >;
}

export interface LinkCandidate {
    _id: string;
    name: string;
    legal_name?: string;
    registration?: { siret?: string; enseigne?: string; naf_code?: string };
    address?: { street?: string; postcode?: string; city?: string };
}

const base = `${API_URL}/admin/place-sources`;

export const getPlaceSourcesStatus = async (): Promise<PlaceSourcesStatus> =>
    (await axios.get(`${base}/status`, { headers: authHeaders() })).data;

/** Upload the .jsonl.gz files built by exportPlaceSources.py; the import then runs in the background. */
export const uploadPlaceFiles = async (files: File[], onProgress?: (pct: number) => void): Promise<{ import: PlaceImportState }> => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    const res = await axios.post(`${base}/import`, form, {
        headers: authHeaders(),
        onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
    });
    return res.data;
};

export const getUnmatchedPlaces = async (params: {
    source?: PlaceSourceKey | "";
    status?: PlacePoolStatus;
    q?: string;
    postcode?: string;
    page?: number;
    limit?: number;
}): Promise<{ items: PooledPlace[]; total: number; page: number; limit: number }> =>
    (await axios.get(`${base}/unmatched`, { params, headers: authHeaders() })).data;

export const searchLinkCandidates = async (q: string, postcode?: string): Promise<LinkCandidate[]> =>
    (await axios.get(`${base}/establishments`, { params: { q, postcode }, headers: authHeaders() })).data.items;

export const linkPlace = async (id: string, establishmentId: string) =>
    (await axios.post(`${base}/unmatched/${id}/link`, { establishmentId }, { headers: authHeaders() })).data;

export const dismissPlace = async (id: string) => (await axios.post(`${base}/unmatched/${id}/dismiss`, {}, { headers: authHeaders() })).data;

export const restorePlace = async (id: string) => (await axios.post(`${base}/unmatched/${id}/restore`, {}, { headers: authHeaders() })).data;
